import { useState, useCallback, useRef } from 'react';

/**
 * Hook for managing edge creation via drag-from-anchor-to-anchor interaction.
 *
 * Usage pattern:
 * 1. User clicks and holds on a source anchor
 * 2. Drags to a target anchor (preview line shown)
 * 3. Releases on valid target to create edge
 *
 * @param {Function} onCreateEdge - Callback when edge is created: (sourceId, targetId) => void
 * @returns {Object} Edge creation state and handlers
 */
export function useEdgeCreation(onCreateEdge) {
  const [isCreating, setIsCreating] = useState(false);
  const [sourceNodeId, setSourceNodeId] = useState(null);
  const [sourceAnchor, setSourceAnchor] = useState(null); // 'left' or 'right'
  const [previewPoint, setPreviewPoint] = useState(null); // { x, y } for preview line end
  const [hoveredTarget, setHoveredTarget] = useState(null); // { nodeId, anchor } when hovering valid target

  const containerRef = useRef(null);

  /**
   * Start creating an edge from a node's anchor
   */
  const startCreating = useCallback((nodeId, anchor, startPoint) => {
    setIsCreating(true);
    setSourceNodeId(nodeId);
    setSourceAnchor(anchor);
    setPreviewPoint(startPoint);
    setHoveredTarget(null);
  }, []);

  /**
   * Update the preview line endpoint as user drags
   */
  const updatePreview = useCallback((point) => {
    if (!isCreating) return;
    setPreviewPoint(point);
  }, [isCreating]);

  /**
   * Set the hovered target anchor (for visual feedback)
   */
  const setTargetHover = useCallback((nodeId, anchor) => {
    if (!isCreating) return;
    // Can't connect to same node
    if (nodeId === sourceNodeId) {
      setHoveredTarget(null);
      return;
    }
    setHoveredTarget({ nodeId, anchor });
  }, [isCreating, sourceNodeId]);

  /**
   * Clear target hover
   */
  const clearTargetHover = useCallback(() => {
    setHoveredTarget(null);
  }, []);

  /**
   * Complete edge creation
   */
  const completeCreation = useCallback((targetNodeId, targetAnchor) => {
    if (!isCreating || !sourceNodeId) return;

    // Don't allow self-connections
    if (targetNodeId === sourceNodeId) {
      cancelCreation();
      return;
    }

    // Determine source and target based on anchor positions
    // Right anchor → Left anchor is the natural flow
    let finalSourceId = sourceNodeId;
    let finalTargetId = targetNodeId;

    // If dragging from left anchor to right anchor, reverse the direction
    if (sourceAnchor === 'left' && targetAnchor === 'right') {
      finalSourceId = targetNodeId;
      finalTargetId = sourceNodeId;
    }

    // Create the edge
    onCreateEdge?.(finalSourceId, finalTargetId);

    // Reset state
    setIsCreating(false);
    setSourceNodeId(null);
    setSourceAnchor(null);
    setPreviewPoint(null);
    setHoveredTarget(null);
  }, [isCreating, sourceNodeId, sourceAnchor, onCreateEdge]);

  /**
   * Cancel edge creation
   */
  const cancelCreation = useCallback(() => {
    setIsCreating(false);
    setSourceNodeId(null);
    setSourceAnchor(null);
    setPreviewPoint(null);
    setHoveredTarget(null);
  }, []);

  return {
    // State
    isCreating,
    sourceNodeId,
    sourceAnchor,
    previewPoint,
    hoveredTarget,
    containerRef,

    // Actions
    startCreating,
    updatePreview,
    setTargetHover,
    clearTargetHover,
    completeCreation,
    cancelCreation
  };
}

export default useEdgeCreation;
